/* eslint-disable react/display-name */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { IFilterParams } from 'ag-grid-community'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import Loader from '../loader'

interface OptionI {
    label: string
    value: string
}

interface GridCustomDropdownFilterProps extends IFilterParams {
    filterOptions: OptionI[]
    multi: boolean
    onApplyFilter: (value: OptionI[] | OptionI | []) => void
    columnKey?: string
    selectedFilters: any
}

const SELECT_ALL_OPTION_VALUE = 'selectAll'

const CustomDropDownFilter = forwardRef((props: GridCustomDropdownFilterProps, ref) => {
    const { t } = useTranslation()
    const experimentApi = useExperimentApi()

    const { columnKey, filterOptions = [], multi = true, selectedFilters, api } = props

    // Memoize filterOptions to prevent infinite loop from prop reference changes
    const memoizedFilterOptions = useMemo(() => filterOptions, [JSON.stringify(filterOptions)])

    const [selectedValue, setSelectedValue] = useState<OptionI[]>([])
    const [prevValue, setprevValue] = useState<OptionI[]>([])
    const [allOptions, setAllOptions] = useState<OptionI[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const selectAllOption: OptionI = useMemo(() => ({ label: t('Select All'), value: SELECT_ALL_OPTION_VALUE }), [t])
    const actualOptions = allOptions.filter((opt) => opt.value !== SELECT_ALL_OPTION_VALUE)
    const filteredActualOptions = actualOptions.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    const filteredOptions = multi && filteredActualOptions.length > 0 ? [selectAllOption, ...filteredActualOptions] : filteredActualOptions
    const isAllSelected = multi && filteredActualOptions.length > 0 && selectedValue.length === filteredActualOptions.length
    const [filterKey, setFilterKey] = useState<string | null>(null)
    const [filterKeyValue, setFilterKeyValue] = useState<any>(null)

    const [filters, setFilters] = useState<string>(JSON.stringify(selectedFilters || {}))

    const cancelFilter = () => {
        props.filterChangedCallback?.()
        setSelectedValue([])
        setSearchTerm('')
        props.onApplyFilter([])
        api.hideColumnFilter()
        //  props.api.destroyFilter(columnKey ?? '')
    }

    const applyFilter = () => {
        props.filterChangedCallback?.()
        const filterValues = selectedValue.filter((opt) => opt.value !== SELECT_ALL_OPTION_VALUE).map((opt) => opt.value)
        props.onApplyFilter(filterValues as any)
        setprevValue(selectedValue)
        api.hideColumnFilter()
    }

    useImperativeHandle(ref, () => ({
        doesFilterPass(params: any) {
            if (!selectedValue || selectedValue.length === 0) return true

            const field = props.colDef.field
            if (!field) return true
            const value = params.data[field]

            if (multi) {
                return selectedValue.some((opt) => opt.value === value)
            } else {
                return selectedValue[0]?.value === value
            }
        },

        getModel() {
            return selectedValue && selectedValue.length > 0 ? { value: selectedValue } : null
        },
        setModel(model: any) {
            setSelectedValue(model?.value ?? [])
        },

        isFilterActive() {
            return selectedValue && selectedValue.length > 0
        },
    }))

    const onCheckboxChange = (option: OptionI) => {
        setSelectedValue((prev) => (prev.some((val) => val.value === option.value) ? prev.filter((val) => val.value !== option.value) : [...prev, option]))
    }

    const mapStringData = (data: any[]): OptionI[] => {
        return data
            .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
            .map((item: string) => ({
                label: item.trim(),
                value: item.trim(),
            }))
    }

    const mapObjectData = (data: any[]): OptionI[] => {
        if (columnKey === 'use_case') {
            return data.filter(Boolean).map((item) => ({
                label: item.title,
                value: item.use_case_literal,
            }))
        }
        if (columnKey === 'experiment_owner') {
            return data.filter(Boolean).map((item) => ({
                label: `${item.first_name} ${item.last_name}`,
                value: item.user_literal,
            }))
        }
        if (columnKey === 'country') {
            return data.filter(Boolean).map((country) => ({
                label: country.title,
                value: country.country_literal,
            }))
        }
        return []
    }

    const getFilterKey = (key: string): string => {
        if (key === 'use_case') return 'use_case_ids'
        if (key === 'experiment_owner') return 'created_by_ids'
        if (key === 'country') return 'country_ids'
        return key
    }

    const onResponseHander = (response: { data: any[] }, filterValues: any = prevValue) => {
        let options: OptionI[] = []
        if (response.data?.length > 0) {
            const isStringData = typeof response.data[0] === 'string' || response.data.every((item) => typeof item === 'string' || item === null)
            options = isStringData ? mapStringData(response.data) : mapObjectData(response.data)
        }
        setAllOptions(multi ? [selectAllOption, ...options] : options)
        setIsLoading(false)
    }

    useEffect(() => {
        if (multi) {
            setAllOptions([selectAllOption, ...memoizedFilterOptions])
        } else {
            setAllOptions(memoizedFilterOptions)
        }
    }, [multi, memoizedFilterOptions])

    useEffect(() => {
        setFilters(JSON.stringify(selectedFilters))
    }, [selectedFilters])

    useEffect(() => {
        if (columnKey) {
            setFilterKey(getFilterKey(columnKey))
            const filterValues = selectedFilters
            if (selectedFilters.created_by_ids === '') {
                selectedFilters.created_by_ids = null
            }

            experimentApi.experimentDropDownData(
                {
                    column_name: columnKey,
                    selectedFilters,
                    paginated: false,
                },
                (response: { data: any[] }) => {
                    onResponseHander(response, filterValues)
                },
                (error) => {
                    setIsLoading(false)
                },
            )
        }
    }, [filters, columnKey])

    useEffect(() => {
        if (selectedFilters && filterKey) {
            setFilterKeyValue(selectedFilters[filterKey])
        }
    }, [selectedFilters, filterKey])

    useEffect(() => {
        if (typeof filterKeyValue === 'string') {
            setSelectedValue(allOptions.filter((opt) => filterKeyValue.split(',').includes(opt.value)))
        } else if (Array.isArray(filterKeyValue)) {
            setSelectedValue(allOptions.filter((opt) => filterKeyValue.includes(opt.value)))
        }
    }, [filterKeyValue, allOptions])

    return (
        <div className="p-4 bg-white border border-gray-300 rounded-lg shadow-lg w-48 min-h-[250px]">
            <div className="mb-5 relative">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('Search')}
                    style={{ backgroundColor: '#f1f1f1' }}
                    className="w-full p-2 pr-8 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
                {searchTerm && (
                    <button type="button" onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black" aria-label={t('Clear search')}>
                        x
                    </button>
                )}
            </div>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <div className="max-h-40 overflow-auto mb-2">
                        <div className="space-y-2">
                            {filteredOptions.length > 0 ? (
                                [...filteredOptions]
                                    .sort((a, b) => {
                                        // Always keep SELECT_ALL_OPTION at the top
                                        if (a.value === SELECT_ALL_OPTION_VALUE) return -1
                                        if (b.value === SELECT_ALL_OPTION_VALUE) return 1

                                        // Then sort checked items before unchecked
                                        const aChecked = selectedValue.some((val) => val.value === a.value)
                                        const bChecked = selectedValue.some((val) => val.value === b.value)
                                        return Number(bChecked) - Number(aChecked)
                                    })
                                    .map((option) => {
                                        if (option.value === SELECT_ALL_OPTION_VALUE) {
                                            return (
                                                <label key={option.value} className="flex items-center w-full cursor-pointer text-sm rounded hover:bg-gray-100 px-1">
                                                    <button
                                                        type="button"
                                                        className={`flex-shrink-0 w-4 h-4 mr-3 border-2 rounded ${isAllSelected ? 'bg-black border-black' : 'border-gray-400 bg-white'} flex items-center justify-center transition-colors`}
                                                        onClick={() => setSelectedValue(isAllSelected ? [] : filteredActualOptions)}
                                                        onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault()
                                                                setSelectedValue(isAllSelected ? [] : filteredActualOptions)
                                                            }
                                                        }}
                                                    >
                                                        {isAllSelected && (
                                                            <svg className="w-2.5 h-2.5 text-white pointer-events-none" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                    <span className="truncate text-xs">{option.label}</span>
                                                </label>
                                            )
                                        }

                                        const isChecked = selectedValue.some((val) => val.value === option.value)

                                        return (
                                            <label key={option.value} className="flex items-center w-full cursor-pointer text-sm rounded hover:bg-gray-100 px-1">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 mr-3 rounded-btn border-gray-400 flex-shrink-0"
                                                    checked={isChecked}
                                                    onChange={() => {
                                                        onCheckboxChange(option)
                                                    }}
                                                />
                                                <span className="truncate text-xs" title={option.label}>
                                                    {option.label}
                                                </span>
                                            </label>
                                        )
                                    })
                            ) : (
                                <div className="text-gray-500 text-sm text-center py-4">{t('No data')}</div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end items-center space-x-2">
                        <button type="button" disabled={selectedValue?.length == 0} onClick={cancelFilter} className="px-5 py-2 rounded-lg disabled:opacity-50 border border-gray-300 text-black" style={{ backgroundColor: '#f1f1f1' }}>
                            {t('Reset')}
                        </button>
                        <button type="button" disabled={selectedValue?.length == 0} onClick={applyFilter} className="px-5 py-2 rounded-lg disabled:opacity-50 border border-gray-300 text-white" style={{ backgroundColor: '#000' }}>
                            {t('Apply')}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
})

export default CustomDropDownFilter
